'use client';

import { PortalProvider } from '@cloai/uikit';
import { HeroUIProvider } from '@heroui/react';

import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';

import {
  ApolloProvider,
  AutoMaskProvider,
  GTMProvider,
  ProfileProvider,
  ImageGenerationProvider,
  ImageGenerationPublishedProvider,
  VideoGenerationProvider,
  StyleProvider,
  SubscriptionsForMeProvider,
  SubscribersForMeProvider,
} from '../providers';
import { Portals } from './(portals)/Portals';
import { PricesProvider } from '../providers/PricesProvider';
import {
  initData as $initData,
  backButton,
  initData,
  retrieveLaunchParams,
  useRawInitData,
  useSignal,
} from '@tma.js/sdk-react';

const mainPaths = ['/profile', '/', '/feed/*'];

// Helper function to check if pathname matches any pattern (including wildcards)
const matchesPath = (pathname: string, patterns: string[]): boolean =>
  patterns.some((pattern) => {
    if (pattern.endsWith('/*')) {
      // Handle wildcard patterns
      const basePath = pattern.slice(0, -2);
      return pathname === basePath || pathname.startsWith(basePath + '/');
    }
    // Exact match
    return pathname === pattern;
  });

export function Providers({ children }: { children: React.ReactNode }) {
  const { back } = useRouter();
  const pathname = usePathname();

  const onBackClick = useCallback(() => {
    if (!matchesPath(pathname, mainPaths)) {
      back();
    }
  }, [back, pathname]);

  useEffect(() => {
    if (backButton.isMounted()) {
      backButton.onClick(onBackClick);
    }

    return () => {
      if (backButton.isMounted()) {
        backButton.offClick(onBackClick);
      }
    };
  }, [onBackClick]);

  useEffect(() => {
    if (!matchesPath(pathname, mainPaths)) {
      if (backButton.isMounted()) {
        backButton.show();
      }
    } else {
      if (backButton.isMounted()) {
        backButton.hide();
      }
    }
  }, [pathname]);

  return (
    <HeroUIProvider>
      <ApolloProvider>
        <ProfileProvider>
          <PricesProvider>
            <SubscriptionsForMeProvider>
              <SubscribersForMeProvider>
                <ImageGenerationProvider>
                  <ImageGenerationPublishedProvider>
                    <VideoGenerationProvider>
                      <GTMProvider>
                        <StyleProvider>
                          <AutoMaskProvider>
                            <PortalProvider>
                              {children}
                              <Portals />
                            </PortalProvider>
                            <ToastContainer pauseOnHover={false} />
                          </AutoMaskProvider>
                        </StyleProvider>
                      </GTMProvider>
                    </VideoGenerationProvider>
                  </ImageGenerationPublishedProvider>
                </ImageGenerationProvider>
              </SubscribersForMeProvider>
            </SubscriptionsForMeProvider>
          </PricesProvider>
        </ProfileProvider>
      </ApolloProvider>
    </HeroUIProvider>
  );
}
